import { ClaudeProposalHotelInfo, Content } from 'ts/shared/interfaces/ProposalData';
import { resolveProposalLanguageFromBranch } from './proposal';

export interface ClaudeHotelInfoConfig {
  language: string;
  details?: boolean;
  overview?: boolean;
  prosCons?: boolean;
  imagesIndexes: number[];
}

export const createDefaultClaudeHotelInfoConfig = (content: Content[]): ClaudeHotelInfoConfig => {
  const language = getDefaultClaudeLanguage(content);
  const selectedContent = content.find((item) => item.language === language);
  const imagesIndexes = (selectedContent?.images || []).slice(0, 1).map((_, index) => index);

  return {
    language,
    details: true,
    overview: true,
    prosCons: false,
    imagesIndexes,
  };
};

export const parseClaudeHotelInfoConfigs = (
  externalParam_1?: string,
): Record<string, ClaudeHotelInfoConfig> => {
  if (!externalParam_1) {
    return {};
  }

  try {
    const params = JSON.parse(externalParam_1);
    if (!params.claudeHotelInfoConfig) {
      return {};
    }

    return typeof params.claudeHotelInfoConfig === 'string'
      ? JSON.parse(params.claudeHotelInfoConfig)
      : params.claudeHotelInfoConfig;
  } catch {
    return {};
  }
};

export const resolveClaudeHotelInfoConfig = (
  configs: Record<string, ClaudeHotelInfoConfig>,
  hotel: any,
  hotelIndex: number,
): ClaudeHotelInfoConfig | undefined => {
  const itemCode = hotel?.hotel?.code;

  if (itemCode && configs[itemCode]) {
    return configs[itemCode];
  }

  const configKeys = Object.keys(configs);
  if (configKeys.length === 1) {
    return configs[configKeys[0]];
  }

  if (hotelIndex < configKeys.length) {
    return configs[configKeys[hotelIndex]];
  }

  return Object.values(configs)[hotelIndex];
};

export const applyClaudeHotelInfoConfigToExternalParam = (
  externalParam_1: string | undefined,
  hotel: any,
  config?: ClaudeHotelInfoConfig,
): string => {
  const itemCode = hotel?.hotel?.code;
  if (!itemCode || !config) {
    return externalParam_1 || '{}';
  }

  let params: Record<string, unknown> = {};
  if (externalParam_1) {
    try {
      params = JSON.parse(externalParam_1);
    } catch {
      params = {};
    }
  }

  const configs = {
    ...parseClaudeHotelInfoConfigs(externalParam_1),
    [itemCode]: config,
  };

  return JSON.stringify({
    ...params,
    claudeHotelInfoConfig: JSON.stringify(configs),
  });
};

export const getClaudeContentByLanguage = (
  aI_HotelInformation?: ClaudeProposalHotelInfo,
  language?: string,
) => {
  if (!aI_HotelInformation?.content?.length) {
    return undefined;
  }

  if (!language) {
    return aI_HotelInformation.content[0];
  }

  return (
    aI_HotelInformation.content.find((item) => item.language === language) ||
    aI_HotelInformation.content[0]
  );
};

const CLAUDE_LANGUAGES = [
  { code: 'en', name: 'English', country: 'US' },
  { code: 'he', name: 'Hebrew', country: 'IL' },
  { code: 'it', name: 'Italian', country: 'IT' },
  { code: 'es', name: 'Spanish', country: 'ES' },
  { code: 'fr', name: 'French', country: 'FR' },
  { code: 'ru', name: 'Russian', country: 'RU' },
  { code: 'de', name: 'German', country: 'DE' },
] as const;

const LANGUAGE_ALIASES: Record<string, string> = {
  italiano: 'it',
  ita: 'it',
  español: 'es',
  espanol: 'es',
  esp: 'es',
  francais: 'fr',
  française: 'fr',
  deutsch: 'de',
  ger: 'de',
  deu: 'de',
  rus: 'ru',
};

const findClaudeLanguage = (language: string) => {
  const key = language.trim().toLowerCase();
  const aliasCode = LANGUAGE_ALIASES[key];

  if (aliasCode) {
    return CLAUDE_LANGUAGES.find((lang) => lang.code === aliasCode);
  }

  return CLAUDE_LANGUAGES.find(
    (lang) =>
      lang.code === key || lang.country.toLowerCase() === key || lang.name.toLowerCase() === key,
  );
};

export const getClaudeI18nLanguage = (language: string) =>
  findClaudeLanguage(language)?.code ?? 'en';

export const getClaudeLanguageCountryCode = (language: string) => {
  const found = findClaudeLanguage(language);
  if (found) {
    return found.country;
  }

  const trimmed = language.trim();
  if (/^[a-zA-Z]{2}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }

  return 'US';
};

export const getClaudeLanguageShortLabel = (language: string) =>
  findClaudeLanguage(language)?.code.toUpperCase() || language.slice(0, 2).toUpperCase();

export const isClaudeEnglishLanguage = (language: string) =>
  findClaudeLanguage(language)?.code === 'en';

export const formatClaudeTextValue = (value: unknown): string => {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    const values = Object.values(value as Record<string, unknown>).filter(
      (item) => typeof item === 'string' && item.trim(),
    );

    return values.join('. ');
  }

  return String(value);
};

export const getDefaultClaudeLanguage = (content: Content[]) => {
  if (!content.length) {
    return '';
  }

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const langKey = resolveProposalLanguageFromBranch(user?.branch?.ext_1_pcc);
  const byI18n = CLAUDE_LANGUAGES.find((lang) => lang.code === langKey)?.name;

  if (byI18n && content.some((item) => item.language === byI18n)) {
    return byI18n;
  }

  const byCode = content.find((item) => item.language.toLowerCase() === langKey);
  if (byCode) {
    return byCode.language;
  }

  const byShortLabel = content.find((item) => findClaudeLanguage(item.language)?.code === langKey);
  if (byShortLabel) {
    return byShortLabel.language;
  }

  return content[0].language;
};
